/**
 * Playbook API client — typed wrappers for PlaybookRule CRUD, toggle,
 * and category listing endpoints.
 *
 * Requirements: 8.1, 8.2, 8.6
 */

import { apiClient } from '@/services/apiClient';
import type { PlaybookRule } from '@/types';

// ── Types ───────────────────────────────────────────────────

export interface PlaybookRuleListParams {
  category?: string;
  severity?: string;
  enabled?: boolean;
  search?: string;
}

export interface CreatePlaybookRuleInput {
  category: string;
  ruleName: string;
  standardPosition: string;
  riskDescription?: string | null;
  severity: string;
  isEnabled?: boolean;
  appliesTo?: string | null;
}

export interface UpdatePlaybookRuleInput {
  category?: string;
  ruleName?: string;
  standardPosition?: string;
  riskDescription?: string | null;
  severity?: string;
  isEnabled?: boolean;
  appliesTo?: string | null;
}

// ── Service ─────────────────────────────────────────────────

export const playbookService = {
  /** GET /api/playbook/rules — list rules with optional filters. */
  listRules(params?: PlaybookRuleListParams): Promise<PlaybookRule[]> {
    const mapped: Record<string, string | number | boolean | undefined> = {};
    if (params?.category) mapped.category = params.category;
    if (params?.severity) mapped.severity = params.severity;
    if (params?.enabled !== undefined) mapped.enabled = params.enabled;
    if (params?.search) mapped.search = params.search;
    return apiClient.get('playbook/rules', mapped);
  },

  /** GET /api/playbook/rules/:id — get a single rule. */
  getRuleById(id: number): Promise<PlaybookRule> {
    return apiClient.get(`playbook/rules/${id}`);
  },

  /** POST /api/playbook/rules — create a new rule. */
  createRule(input: CreatePlaybookRuleInput): Promise<PlaybookRule> {
    return apiClient.post('playbook/rules', input);
  },

  /** PUT /api/playbook/rules/:id — update a rule. */
  updateRule(id: number, input: UpdatePlaybookRuleInput): Promise<PlaybookRule> {
    return apiClient.put(`playbook/rules/${id}`, input);
  },

  /** DELETE /api/playbook/rules/:id — delete a rule. */
  deleteRule(id: number): Promise<void> {
    return apiClient.delete(`playbook/rules/${id}`);
  },

  /** PUT /api/playbook/rules/:id/toggle — enable/disable a rule. */
  toggleRule(id: number, enabled: boolean): Promise<PlaybookRule> {
    return apiClient.put(`playbook/rules/${id}/toggle`, { enabled });
  },

  /** GET /api/playbook/categories — list the 8 predefined categories. */
  listCategories(): Promise<string[]> {
    return apiClient.get('playbook/categories');
  },
};
